/**
 * IndexedDB FIFO queue for GraphQL writes and S3 uploads made while offline.
 * GraphQL jobs are also mirrored to localStorage so a refresh cannot drop them
 * if the IndexedDB transaction is aborted during unload.
 */
import { readJwtUserId } from "./cache";
import { QUEUE_BACKUP_PREFIX, STORE_SYNC } from "./constants";
import { idbAdd, idbClear, idbDelete, idbGetAll, idbPut } from "./idb";
import { combineInspectInput, flattenInspectInputs, inspectGroupKey, isModuleInspectJob } from "./inspectQueue";

let hydrated = false;

/**
 * Replaces every occurrence of `from` inside a JSON-like value.
 *
 * @param {*} value Variables or nested field.
 * @param {string} from Search string.
 * @param {string} to Replacement.
 * @returns {*} Cloned value with replacements.
 */
export const replaceDeep = (value, from, to) => {
  if (from == null || from === "" || to == null) return value;
  if (typeof value === "string") return value.split(from).join(to);
  if (Array.isArray(value)) return value.map((item) => replaceDeep(item, from, to));
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      next[key] = replaceDeep(value[key], from, to);
    });
    return next;
  }
  return value;
};

/**
 * Stable identity for a GraphQL queue row (no IndexedDB id required).
 *
 * @param {Object} job Queue record.
 * @returns {string}
 */
export const gqlJobFingerprint = (job) =>
  `gql|${Number(job?.createdAt) || 0}|${String(job?.query || "")}|${JSON.stringify(job?.variables || {})}`;

/**
 * localStorage key for the signed-in learner's GraphQL backup.
 *
 * @returns {string}
 */
const backupKey = () => `${QUEUE_BACKUP_PREFIX}${readJwtUserId()}`;

/**
 * JSON-safe GraphQL jobs (no S3 ArrayBuffers).
 *
 * @param {Array<Object>} jobs Queue records.
 * @returns {Array<Object>}
 */
const gqlJobsOnly = (jobs) =>
  (jobs || [])
    .filter((job) => job && job.kind === "gql")
    .map((job) => ({
      kind: "gql",
      query: job.query,
      variables: job.variables,
      createdAt: job.createdAt,
    }));

/**
 * Reads the durable GraphQL backup for this user.
 *
 * @returns {Array<Object>}
 */
export const readGqlQueueBackup = () => {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(backupKey());
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? gqlJobsOnly(parsed) : [];
  } catch {
    return [];
  }
};

/**
 * Writes the GraphQL backup synchronously so it survives a refresh.
 *
 * @param {Array<Object>} jobs Queue records.
 * @returns {void}
 */
export const writeGqlQueueBackup = (jobs) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(backupKey(), JSON.stringify(gqlJobsOnly(jobs)));
  } catch {
    /* quota / private mode */
  }
};

/**
 * Drops the GraphQL backup for the current user.
 *
 * @returns {void}
 */
export const clearGqlQueueBackup = () => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(backupKey());
  } catch {
    /* private mode */
  }
};

/**
 * Unions IndexedDB GraphQL jobs with the localStorage backup without duplicates.
 *
 * @param {Array<Object>} idbJobs Jobs already in IndexedDB.
 * @param {Array<Object>} backupJobs Jobs from localStorage.
 * @returns {Array<Object>} Backup jobs that are missing from IndexedDB.
 */
export const missingBackupJobs = (idbJobs, backupJobs) => {
  const seen = new Set(gqlJobsOnly(idbJobs).map(gqlJobFingerprint));
  return gqlJobsOnly(backupJobs).filter((job) => !seen.has(gqlJobFingerprint(job)));
};

/**
 * Reads queued jobs in insertion order (IndexedDB only).
 *
 * @returns {Promise<Array>}
 */
const listIdbQueue = async () => {
  const rows = await idbGetAll(STORE_SYNC);
  return rows.slice().sort((a, b) => (a.id || 0) - (b.id || 0) || (a.createdAt || 0) - (b.createdAt || 0));
};

/**
 * Mirrors GraphQL jobs to localStorage after IndexedDB changes.
 *
 * @returns {Promise<Array>}
 */
const persistGqlBackup = async () => {
  const jobs = await listIdbQueue().catch(() => []);
  writeGqlQueueBackup(jobs);
  return jobs;
};

/**
 * Replays localStorage GraphQL jobs into IndexedDB after a refresh that
 * aborted in-flight IDB transactions.
 *
 * @returns {Promise<Array>}
 */
export const hydrateSyncQueueFromBackup = async () => {
  if (hydrated) return listIdbQueue().catch(() => []);
  const backup = readGqlQueueBackup();
  let rows = await listIdbQueue().catch(() => []);
  const missing = missingBackupJobs(rows, backup);
  for (const job of missing) {
    try {
      await idbAdd(STORE_SYNC, {
        kind: "gql",
        query: job.query,
        variables: job.variables,
        createdAt: job.createdAt || Date.now(),
      });
    } catch {
      /* store missing / quota */
    }
  }
  rows = await listIdbQueue().catch(() => []);
  const leftover = missingBackupJobs(rows, backup);
  if (leftover.length) {
    // Keep the backup if IndexedDB could not take every row (quota / abort).
    return rows;
  }
  hydrated = true;
  writeGqlQueueBackup(rows);
  return rows;
};

/**
 * Enqueues a GraphQL write for later replay.
 * Consecutive `module_inspect_edit` ticks for the same course/module are merged
 * so reconnect sends one combined mutation instead of one call per minute.
 *
 * @param {string} query GraphQL document.
 * @param {Object} [variables={}] Variables.
 * @returns {Promise<IDBValidKey>}
 */
export const enqueueGql = async (query, variables = {}) => {
  await hydrateSyncQueueFromBackup();
  const createdAt = Date.now();
  const draft = { kind: "gql", query, variables, createdAt };
  writeGqlQueueBackup([...readGqlQueueBackup(), draft]);

  if (isModuleInspectJob({ kind: "gql", query })) {
    const [input] = flattenInspectInputs([variables?.input]);
    const key = inspectGroupKey(input);
    if (key && key !== "::") {
      const jobs = await listIdbQueue();
      const existing = jobs.find((job) => isModuleInspectJob(job) && inspectGroupKey(flattenInspectInputs([job.variables?.input])[0]) === key);
      if (existing) {
        const [prev] = flattenInspectInputs([existing.variables?.input]);
        await idbPut(STORE_SYNC, {
          ...existing,
          variables: { input: [combineInspectInput(prev, input)] },
        });
        await persistGqlBackup();
        return existing.id;
      }
      const id = await idbAdd(STORE_SYNC, {
        kind: "gql",
        query,
        variables: { input: [input] },
        createdAt,
      });
      await persistGqlBackup();
      return id;
    }
  }

  const id = await idbAdd(STORE_SYNC, draft);
  await persistGqlBackup();
  return id;
};

/**
 * Enqueues a file upload (ArrayBuffer) for later replay.
 *
 * @param {Object} item Upload metadata plus `buffer`.
 * @returns {Promise<IDBValidKey>}
 */
export const enqueueS3 = (item) =>
  idbAdd(STORE_SYNC, {
    kind: "s3",
    createdAt: Date.now(),
    ...item,
  });

/**
 * Reads queued jobs in insertion order, restoring any GraphQL backup first.
 *
 * @returns {Promise<Array>}
 */
export const listSyncQueue = async () => {
  await hydrateSyncQueueFromBackup();
  return listIdbQueue();
};

/**
 * Removes one queued job and updates the GraphQL backup.
 *
 * @param {IDBValidKey} id Job id.
 * @returns {Promise<*>}
 */
export const deleteSyncJob = async (id) => {
  const result = await idbDelete(STORE_SYNC, id);
  await persistGqlBackup();
  return result;
};

/**
 * Drops every queued job (IndexedDB + GraphQL backup). Used on logout.
 *
 * @returns {Promise<void>}
 */
export const clearSyncQueue = async () => {
  hydrated = false;
  // Wipe the JWT-scoped backup first, while the token is still present.
  clearGqlQueueBackup();
  try {
    await idbClear(STORE_SYNC);
  } catch {
    /* store missing */
  }
};

/**
 * After an S3 upload succeeds, rewrite blob/object URLs in remaining GraphQL jobs.
 *
 * @param {string} localUrl Object URL returned while offline.
 * @param {string} publicUrl Live S3 URL.
 * @returns {Promise<void>}
 */
export const rewriteQueuedBlobUrls = async (localUrl, publicUrl) => {
  if (!localUrl || !publicUrl || localUrl === publicUrl) return;
  const items = await listIdbQueue();
  await Promise.all(
    items
      .filter((item) => item.kind === "gql")
      .map(async (item) => {
        const next = replaceDeep(item.variables, localUrl, publicUrl);
        if (JSON.stringify(next) !== JSON.stringify(item.variables)) {
          await idbPut(STORE_SYNC, { ...item, variables: next });
        }
      })
  );
  await persistGqlBackup();
};
